import {
  Children,
  isValidElement,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { COLUNA_DE_TEXTO_MINIMA, multiploDeQuatro, trilhasDaFaixa } from './faixa';

/**
 * Faixa de indicadores (v2.1, p.25): no maximo QUATRO.
 *
 * O teto nao e estetico. Quatro e o que se le de relance antes de a pessoa
 * comecar a procurar; a partir dai o painel vira parede de numero e ninguem
 * olha nenhum. A regra do manual e explicita, e a ela se soma a outra:
 * "se ninguem pergunta aquilo de manha, o indicador sai da tela".
 *
 * ALINHAMENTO PERFEITO — como, e nao so que
 * `grid-rows-subgrid` faz os cards compartilharem as MESMAS quatro linhas da
 * faixa: icone e rotulo, valor e variacao, contexto, barra. Assim o valor de um card fica na
 * mesma altura do valor do vizinho mesmo quando o rotulo de um deles quebra em
 * duas linhas. Sem subgrid, cada card se resolveria sozinho e os numeros
 * ficariam escalonados — que e exatamente o defeito que faz a comparacao
 * visual entre indicadores se perder. Com a faixa em duas fileiras, cada
 * fileira alinha as suas quatro linhas.
 *
 * A FAIXA SE REORGANIZA EM VEZ DE ESPREMER (R01, p.28)
 * Quatro colunas fixas davam ~78 px por card em 390 ("3." no lugar de 3.030)
 * e cortavam "R$ 15.963,93" em 1280 e 1024. A primeira correcao so olhava o
 * VALOR: com numero curto a faixa ficava em quatro colunas a 1024, com 132 px
 * de texto — rotulo em duas linhas, contexto em quatro e card com o dobro da
 * altura. Agora o card pede a largura do que ele mostra, MEDIDA no navegador
 * na fonte real: o valor inteiro, o rotulo inteiro numa linha (como ele esta
 * escrito agora, com a caixa que estiver valendo) e uma coluna de texto de
 * pelo menos `COLUNA_DE_TEXTO_MINIMA` para o contexto. Quando nao cabe, a
 * faixa cai de coluna (`trilhasDaFaixa`). A medida se refaz quando a faixa
 * muda de largura, quando a fonte termina de carregar e quando um rotulo
 * muda de tamanho — o texto da interface em caixa alta, por exemplo.
 *
 * Antes da primeira medida vale a estimativa pelo valor, em CSS
 * (`faixa-indicadores`).
 */

const MAXIMO_DE_INDICADORES = 4;

/* A ESTIMATIVA DE ANTES DA MEDIDA, em px. "R$ 15.963,93" ocupa ~190 px em
   Sora 28/700 (12 caracteres, ~16 px cada; conta-se 19 para o digito largo
   nao passar). A unidade e IBM Plex Sans 13 (~8 px por caractere). A moldura
   e o que o card poe em volta do texto: 16 + 16 de padding, 2 de borda, 36 do
   bloco do icone e 12 de vao. */
const PX_POR_CARACTERE_DO_VALOR = 19;
const PX_POR_CARACTERE_DA_UNIDADE = 8;
const MOLDURA_DO_CARD = 82;
const LARGURA_MINIMA_DO_CARD = multiploDeQuatro(MOLDURA_DO_CARD + COLUNA_DE_TEXTO_MINIMA);

/* Folga do rotulo medido: o arredondamento de subpixel nao pode ser o que
   decide se ele quebra. */
const FOLGA_DO_ROTULO = 4;

function larguraEstimada(filho: ReactNode): number {
  if (!isValidElement(filho)) return LARGURA_MINIMA_DO_CARD;
  const props = filho.props as { valor?: unknown; unidade?: unknown; unidadeColada?: unknown };
  if (typeof props.valor !== 'string') return LARGURA_MINIMA_DO_CARD;
  const unidade = typeof props.unidade === 'string' ? props.unidade : '';
  const espaco = unidade && props.unidadeColada !== true ? 1 : 0;
  const texto =
    props.valor.length * PX_POR_CARACTERE_DO_VALOR + (unidade.length + espaco) * PX_POR_CARACTERE_DA_UNIDADE;
  return Math.max(LARGURA_MINIMA_DO_CARD, multiploDeQuatro(MOLDURA_DO_CARD + texto));
}

/** O que muda a conta: o rotulo, o valor e a unidade de cada card. */
function assinaturaDosCards(filhos: readonly ReactNode[]): string {
  return filhos
    .map((filho) => {
      if (!isValidElement(filho)) return '';
      const props = filho.props as { rotulo?: unknown; valor?: unknown; unidade?: unknown };
      return [props.rotulo, props.valor, props.unidade].map((parte) => String(parte ?? '')).join('|');
    })
    .join('§');
}

let tela: CanvasRenderingContext2D | null | undefined;

/** Largura do texto numa linha so, na fonte e na caixa em que ele esta desenhado. */
function larguraDoTextoNumaLinha(elemento: Element): number {
  const estilo = getComputedStyle(elemento);
  const bruto = elemento.textContent ?? '';
  const texto =
    estilo.textTransform === 'uppercase'
      ? bruto.toLocaleUpperCase('pt-BR')
      : estilo.textTransform === 'lowercase'
        ? bruto.toLocaleLowerCase('pt-BR')
        : bruto;
  if (tela === undefined) tela = document.createElement('canvas').getContext('2d');
  if (!tela) return elemento.getBoundingClientRect().width;
  tela.font = `${estilo.fontStyle} ${estilo.fontWeight} ${estilo.fontSize} ${estilo.fontFamily}`;
  const entreletra = Number.parseFloat(estilo.letterSpacing);
  return tela.measureText(texto).width + (Number.isFinite(entreletra) ? entreletra * texto.length : 0);
}

/** A largura que o card mais exigente da faixa pede, medida. `null` se ainda nao ha o que medir. */
function larguraMedida(faixa: HTMLElement): number | null {
  let pedida = 0;
  for (const card of faixa.children) {
    const rotulo = card.querySelector('.kpi-rotulo');
    const valor = card.querySelector('.kpi-valor');
    if (!rotulo || !valor) continue;
    const colunaDeTexto = rotulo.getBoundingClientRect().width;
    const moldura = card.getBoundingClientRect().width - colunaDeTexto;
    /* O valor nao quebra: a caixa do texto dele e a largura de verdade. */
    const faixaDoValor = document.createRange();
    faixaDoValor.selectNodeContents(valor);
    const larguraDoValor = faixaDoValor.getBoundingClientRect().width;
    const texto = Math.max(
      COLUNA_DE_TEXTO_MINIMA,
      larguraDoTextoNumaLinha(rotulo) + FOLGA_DO_ROTULO,
      larguraDoValor,
    );
    pedida = Math.max(pedida, multiploDeQuatro(moldura + texto));
  }
  return pedida > 0 ? pedida : null;
}

export function FaixaIndicadores({ children }: { readonly children: ReactNode }) {
  const filhos = Children.toArray(children);
  const quantidade = filhos.length;
  const faixa = useRef<HTMLDivElement>(null);
  const [trilhas, setTrilhas] = useState<number | null>(null);
  const assinatura = assinaturaDosCards(filhos);

  /* Verificador da invariante, em desenvolvimento. O manual manda no maximo
     quatro; sem um aviso, o quinto card entra numa tela qualquer numa terca e
     ninguem percebe ate a revisao. */
  if (import.meta.env.DEV && quantidade > MAXIMO_DE_INDICADORES) {
    console.error(
      `FaixaIndicadores: ${quantidade} indicadores. O manual (p.25) permite no máximo ${MAXIMO_DE_INDICADORES} por faixa.`,
    );
  }

  /* Antes da pintura: a primeira imagem ja sai na grade certa. */
  useLayoutEffect(() => {
    const no = faixa.current;
    if (!no) return;
    let vivo = true;
    function medir() {
      if (!vivo || !no) return;
      const minimo = larguraMedida(no);
      if (minimo === null) return;
      const vao = Number.parseFloat(getComputedStyle(no).columnGap) || 0;
      const proximas = trilhasDaFaixa({ largura: no.clientWidth, quantidade, minimo, vao });
      setTrilhas((atuais) => (atuais === proximas ? atuais : proximas));
    }
    medir();
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    observador?.observe(no);
    for (const rotulo of no.querySelectorAll('.kpi-rotulo')) observador?.observe(rotulo);
    void document.fonts?.ready.then(medir);
    return () => {
      vivo = false;
      observador?.disconnect();
    };
  }, [quantidade, assinatura]);

  const medidas = {
    '--faixa-n': Math.min(Math.max(quantidade, 1), MAXIMO_DE_INDICADORES),
    '--faixa-coluna-min': `${String(Math.max(LARGURA_MINIMA_DO_CARD, ...filhos.map(larguraEstimada)))}px`,
    ...(trilhas === null ? {} : { gridTemplateColumns: `repeat(${String(trilhas)}, minmax(0, 1fr))` }),
  } as CSSProperties;

  return (
    <div ref={faixa} className="faixa-indicadores" style={medidas}>
      {children}
    </div>
  );
}
